import React from 'react';

import { useTranslations } from 'next-intl';

import { ActionIcon, Badge, Box, Group, Paper, PaperProps, Text, Textarea } from '@mantine/core';
import { IconCloudUpload, IconFileText, IconFileTypeCsv, IconFileTypeDocx, IconFileTypePdf, IconPaperclip, IconPhoto, IconSend, IconSquareFilled, IconX } from '@tabler/icons-react';
import { motion } from 'framer-motion';

import { useFileProcessor } from '@hooks/chatbot/useFileProcessor';

export type ChatInputSize = 'sm' | 'md' | 'lg';

export interface ChatInputProps {
    inputRef: React.RefObject<HTMLInputElement | null>;
    input: string;
    setInput: (val: string) => void;
    handleSubmit: (e: React.FormEvent) => void;
    isLoading: boolean;
    isExecutingTool: boolean;
    stop: () => void;
    quota: { max: number; remaining: number | null } | null;
    size?: ChatInputSize;
    formStyle?: React.CSSProperties;
    paperProps?: PaperProps;
    placeholder?: string;
    attachments?: import('@repo/contracts').ChatAttachment[];
    setAttachments?: (atts: import('@repo/contracts').ChatAttachment[]) => void;
    isGlobalDragging?: boolean;
    layoutId?: string;
    absoluteAttachments?: boolean;
}

export function ChatInput({
    inputRef,
    input,
    setInput,
    handleSubmit,
    isLoading,
    isExecutingTool,
    stop,
    quota,
    size = 'md',
    formStyle,
    paperProps,
    placeholder,
    attachments,
    setAttachments,
    isGlobalDragging,
    layoutId,
    absoluteAttachments,
}: ChatInputProps) {
    const t = useTranslations();
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const { processFiles } = useFileProcessor(attachments, setAttachments);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            await processFiles(files);
        }
        e.target.value = '';
    };

    const getFileIcon = (type: string, name?: string) => {
        if (type === 'application/pdf' || name?.toLowerCase().endsWith('.pdf')) return <IconFileTypePdf size={14} color="var(--mantine-color-red-6)" />;
        if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || name?.toLowerCase().endsWith('.docx')) return <IconFileTypeDocx size={14} color="var(--mantine-color-blue-6)" />;
        if (type.startsWith('image/') || name?.match(/\.(png|jpe?g|webp)$/i)) return <IconPhoto size={14} color="var(--mantine-color-blue-6)" />;
        if (type.includes('csv') || name?.toLowerCase().endsWith('.csv')) return <IconFileTypeCsv size={14} color="var(--mantine-color-green-6)" />;
        if (type.startsWith('text/') || name?.match(/\.(txt|md)$/i)) return <IconFileText size={14} color="var(--mantine-color-gray-6)" />;
        return <IconPaperclip size={14} />;
    };

    // Map size to specific layout properties
    const styleConfig = {
        sm: {
            paperPadding: 4,
            inputFontSize: undefined,
            rightSectionWidth: 38,
            iconButtonSize: 'md',
            iconSize: 16,
            quotaMt: 6,
        },
        md: {
            paperPadding: 6,
            inputFontSize: '1rem',
            rightSectionWidth: 46,
            iconButtonSize: 'lg',
            iconSize: 18,
            quotaMt: 8,
        },
        lg: {
            paperPadding: 'xl',
            inputFontSize: '1.1rem',
            rightSectionWidth: 60,
            iconButtonSize: 'lg',
            iconSize: 20,
            quotaMt: 'md',
        },
    }[size];

    React.useEffect(() => {
        if (!isLoading && !isExecutingTool) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isLoading, isExecutingTool, inputRef]);

    return (
        <form 
            onSubmit={handleSubmit} 
            style={{ ...formStyle, position: 'relative' }}
        >
            {isGlobalDragging && (
                <div
                    style={{
                        position: 'absolute',
                        top: -10, left: -10, right: -10, bottom: -10,
                        borderRadius: 'calc(var(--mantine-radius-xl) + 4px)',
                        zIndex: 10,
                        pointerEvents: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        overflow: 'hidden',
                    }}
                >
                    {/* Background Layer with opacity and gradient */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(135deg, var(--mantine-color-nli-liberty-4), var(--mantine-color-nli-pink-lavender-4))',
                            opacity: 0.6,
                            border: '2px dashed var(--mantine-color-nli-liberty-4)',
                            borderRadius: 'calc(var(--mantine-radius-xl) + 4px)',
                            backdropFilter: 'blur(4px)',
                            zIndex: -1,
                        }}
                    />

                    <IconCloudUpload size={32} stroke={1.5} style={{ marginBottom: 4 }} />
                    <Text fw={600} size={size} c="white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                        {t('add-file-to-conversation', { fallback: 'Ajouter le fichier à la conversation' })}
                    </Text>
                </div>
            )}
            <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                style={{ display: 'none' }} 
                accept="application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/plain, text/csv, text/markdown, image/png, image/jpeg, image/jpg, image/webp, .txt, .csv, .md, .png, .jpg, .jpeg, .webp, .docx"
                multiple
            />
            {attachments && attachments.length > 0 && setAttachments && (
                <Box style={absoluteAttachments ? { position: 'absolute', bottom: '100%', left: 0, right: 0, paddingBottom: 8, zIndex: 10 } : undefined}>
                    <Group gap="xs" mb={absoluteAttachments ? 0 : 'xs'}>
                        {attachments.map((att, idx) => (
                            <Badge 
                                key={idx} 
                                color="nli-yankees-blue" 
                                variant="light" 
                                leftSection={getFileIcon(att.type, att.name)}
                                rightSection={<IconX size={12} style={{ cursor: 'pointer' }} onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))} />}
                                style={{ textTransform: 'none' }}
                                maw={200}
                                title={att.name}
                            >
                                {att.name}
                            </Badge>
                        ))}
                    </Group>
                </Box>
            )}
            
            <Box component={(layoutId ? motion.div : 'div') as any} {...(layoutId ? { layoutId } : {})} style={{ position: 'relative' }}>
                <Paper 
                    shadow="sm" 
                    p={styleConfig.paperPadding}
                    radius="xl"
                    withBorder
                    style={{
                        border: '1px solid var(--mantine-color-nli-liberty-2)',
                        background: 'linear-gradient(to right, rgba(132, 94, 247, 0.05), rgba(246, 172, 200, 0.05))',
                        ...paperProps?.style,
                    }}
                    {...paperProps}
                >
                    <Textarea
                        ref={inputRef as React.RefObject<any>}
                        size={size === 'lg' ? 'lg' : size === 'md' ? 'md' : 'sm'}
                        radius="xl"
                        autosize
                        minRows={1}
                        maxRows={6}
                        placeholder={quota?.remaining === 0 ? t('quota-exceeded') : (placeholder || t('chatbot-input-placeholder'))}
                        value={input}
                        onChange={(e) => setInput(e.currentTarget.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (!isLoading && !isExecutingTool && input.trim()) {
                                    handleSubmit(e as any);
                                }
                            }
                        }}
                        disabled={isLoading || quota?.remaining === 0}
                        styles={{
                            input: {
                                border: 'none',
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                fontSize: styleConfig.inputFontSize,
                            },
                        }}
                        leftSectionWidth={styleConfig.rightSectionWidth}
                        leftSection={setAttachments ? (
                            <motion.div layout>
                                <ActionIcon 
                                    className='btn-gradient-hover'
                                    size={styleConfig.iconButtonSize}
                                    radius="xl"
                                    variant='gradient'
                                    gradient={{ from: 'nli-liberty.6', to: 'nli-pink-lavender.6', deg: 45 }}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isLoading || quota?.remaining === 0}
                                >
                                    <IconPaperclip size={styleConfig.iconSize} />
                                </ActionIcon>
                            </motion.div>
                        ) : undefined}
                        rightSectionWidth={styleConfig.rightSectionWidth}
                        rightSection={
                            <motion.div layout>
                                <ActionIcon 
                                    className="btn-gradient-hover"
                                    size={styleConfig.iconButtonSize}
                                    radius="xl"
                                    type={isLoading ? 'button' : 'submit'}
                                    variant="gradient"
                                    gradient={{ from: 'nli-liberty.6', to: 'nli-pink-lavender.6', deg: 45 }}
                                    aria-label={isLoading ? t('stop') : t('send')}
                                    onClick={isLoading ? stop : undefined}
                                    disabled={(!isLoading && !input.trim()) || isExecutingTool}
                                >
                                    {(isLoading && !isExecutingTool) ? <IconSquareFilled size={styleConfig.iconSize} /> : <IconSend size={styleConfig.iconSize} /> }
                                </ActionIcon>
                            </motion.div>
                        }
                        autoFocus
                    />
                </Paper>
            </Box>
            
            {quota && quota.max > 0 && quota.remaining !== null && (
                <Text size={size === 'lg' ? 'sm' : 'xs'} c={quota.remaining === 0 ? 'red' : 'dimmed'} mt={styleConfig.quotaMt} ta="center">
                    {quota.remaining}
                    {' / '}
                    {quota.max} 
                    {' '}
                    {t('messages-remaining-today')}
                </Text>
            )}
        </form>
    );
}
